import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronIcon, ErrorState, LoadingState } from "@signet/ui";
import { CreatorAvatar, useCreatorPhotos } from "@signet/web-kit";
import { BASE, dayLabel, fetchMonths, monthLabel, subsKeys, type Months } from "./api.ts";
import { Change } from "./board.tsx";
import { SubsTabs } from "./page.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Month on month (the boss, 2026-10-13): how many subs each creator took in
   a month, on each page, against the month before, so "sales are down" can
   be checked against whether the subs went down too. A month still running
   is compared with the same days of the month before.
   ────────────────────────────────────────────────────────────────────────── */

const shiftMonth = (month: string, by: number) => {
  const [y, m] = month.split("-").map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1 + by, 1)).toISOString().slice(0, 7);
};

const number = (n: number | null) => (n === null ? <span className="subs-faint">-</span> : n.toLocaleString());

const lastDay = (month: string) => {
  const [y, m] = month.split("-").map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
};

const short = (day: string) => new Date(`${day}T12:00:00Z`).toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "UTC" });

/** Still running: only part of the month is in. */
const running = (data: Months) => data.through < lastDay(data.month);

function versusLabel(data: Months) {
  return running(data) ? `vs ${short(data.previousFrom)} to ${short(data.previousTo)}` : `vs ${monthLabel(data.previousFrom.slice(0, 7))}`;
}

function History({ data }: { data: Months }) {
  const months = data.history.slice(-12);
  if (months.length < 2) return null;
  const peak = Math.max(1, ...months.map((m) => m.total));
  return (
    <div className="subs-history" role="list" aria-label="Team subs by month">
      {months.map((m) => (
        <div key={m.month} role="listitem" className={`subs-history-bar${m.month === data.month ? " on" : ""}`} title={`${monthLabel(m.month)}: ${m.total.toLocaleString()}`}>
          <b className="tabular">{m.total.toLocaleString()}</b>
          <span className="subs-history-track">
            <span style={{ height: `${Math.max(3, (Math.max(0, m.total) / peak) * 100)}%` }} />
          </span>
          <small>{monthLabel(m.month).slice(0, 3)}</small>
        </div>
      ))}
    </div>
  );
}

export function MonthsPage() {
  const search = useSearch({ strict: false } as never) as { month?: string };
  const navigate = useNavigate();
  const months = useQuery({ queryKey: subsKeys.months(search.month ?? "now"), queryFn: () => fetchMonths(search.month) });
  const photos = useCreatorPhotos(BASE);
  const data = months.data;
  const thisMonth = new Date().toISOString().slice(0, 7);
  const month = data?.month ?? search.month ?? thisMonth;
  const go = (next: string) => navigate({ to: ".", search: { month: next } as never, replace: true });
  const versus = data ? versusLabel(data) : "";

  return (
    <div className="page subs-page">
      <header className="page-head">
        <div>
          <h1>Subs by month</h1>
          <p>Each creator's subs for the month on every page, against the month before.</p>
        </div>
      </header>
      <SubsTabs on="months" />

      <div className="subs-bar">
        <div className="subs-day">
          <button type="button" className="icon-button" aria-label="Month before" onClick={() => go(shiftMonth(month, -1))}>
            <ChevronIcon size={15} direction="left" />
          </button>
          <span className="subs-day-pick">
            <span>{monthLabel(month)}</span>
          </span>
          <button type="button" className="icon-button" aria-label="Month after" disabled={shiftMonth(month, 1) > thisMonth} onClick={() => go(shiftMonth(month, 1))}>
            <ChevronIcon size={15} direction="right" />
          </button>
        </div>
        {data && running(data) ? <span className="subs-faint">So far, to {dayLabel(data.through)}</span> : null}
      </div>

      {months.isLoading ? (
        <LoadingState />
      ) : months.isError || !data ? (
        <ErrorState title="Couldn't load the months" onRetry={() => months.refetch()} />
      ) : (
        <>
          <div className="subs-cards">
            <div className="panel subs-card">
              <span>Total subs</span>
              <b className="tabular">{data.totals.total.toLocaleString()}</b>
              <small>
                <Change now={data.totals.total} before={data.previousTotal} /> {versus}
              </small>
            </div>
            <div className="panel subs-card">
              <span>Free</span>
              <b className="tabular">{data.totals.free?.toLocaleString() ?? 0}</b>
            </div>
            <div className="panel subs-card">
              <span>VIP</span>
              <b className="tabular">{data.totals.vip?.toLocaleString() ?? 0}</b>
            </div>
            <div className="panel subs-card">
              <span>Third page</span>
              <b className="tabular">{data.totals.third?.toLocaleString() ?? 0}</b>
            </div>
          </div>

          <History data={data} />

          <section className="panel subs-section">
            <div className="subs-table-wrap">
              <table className="subs-table">
                <thead>
                  <tr>
                    <th className="subs-rank">#</th>
                    <th className="subs-who">Creator</th>
                    <th>Total</th>
                    <th>Free</th>
                    <th>VIP</th>
                    <th>Third page</th>
                    <th>Before</th>
                    <th>Change</th>
                    <th>Days in</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={r.id}>
                      <td className="subs-rank tabular">{i + 1}</td>
                      <td className="subs-who">
                        <span>
                          <CreatorAvatar name={r.creator} src={photos(r.creator)} size={26} />
                          <strong>{r.creator}</strong>
                          {r.platform === "fansly" ? <span className="subs-platform">Fansly</span> : null}
                        </span>
                      </td>
                      <td className="subs-total tabular">{r.total.toLocaleString()}</td>
                      <td className="tabular">{number(r.free)}</td>
                      <td className="tabular">{number(r.vip)}</td>
                      <td className="tabular">{number(r.third)}</td>
                      <td className="tabular">{number(r.previous)}</td>
                      <td>
                        <Change now={r.total} before={r.previous} />
                      </td>
                      <td className="tabular subs-faint">{r.days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!data.rows.length ? <p className="subs-none">Nothing entered for {monthLabel(month)}.</p> : null}
            </div>
          </section>
          <p className="subs-faint subs-note">Before is {versus.replace(/^vs /, "")}, so a month still running is never compared with a whole one.</p>
        </>
      )}
    </div>
  );
}
